import { ComponentType } from "@/store/slices/componentSlice";
import { ListContentType, PermissionButtonState } from "@/types/ContentTypes";
import { ListGroup } from "@/types/Groups";
import { JSX } from "react";
import { FinancialInfoNEW } from "@/types/BusinessInfoNEW";

export const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

type SvgMap = {
  [key: string]: JSX.Element;
};

const svgMap: SvgMap = {
  view: (
    <svg
      className="w-6 h-6 text-gray-800 dark:text-white"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        stroke="currentColor"
        strokeWidth="2"
        d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z"
      />
      <path
        stroke="currentColor"
        strokeWidth="2"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  ),
  add: (
    <svg
      className="w-6 h-6 text-gray-800 dark:text-white"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 12h14m-7 7V5"
      />
    </svg>
  ),
  change: (
    <svg
      className="w-6 h-6 text-gray-800 dark:text-white"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
      />
    </svg>
  ),
  delete: (
    <svg
      className="w-6 h-6 text-gray-800 dark:text-white"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
      />
    </svg>
  ),
};

export const mapIconToPermission = (permissionName: string): JSX.Element => {
  const key = permissionName.split("_")[0];
  return svgMap[key];
};

export const generatePermissionsMap = (
  contentTypes: ListContentType[] | null,
  group: ListGroup
): PermissionButtonState => {
  const permissionState: PermissionButtonState = {};

  if (!contentTypes) return permissionState;

  for (const contentType of contentTypes) {
    for (const permission of contentType.permissions) {
      permissionState[permission] =
        group.permissions?.find((perm) => perm === permission) !== undefined;
    }
  }

  return permissionState;
};

export const mapSopToBoolean = (
  sopData: string[] | null,
  SopData: string[]
) => {
  const result: { [key: string]: boolean } = {};

  for (const data of SopData) {
    result[data] = sopData?.find((sp) => sp === data) !== undefined;
  }

  return result;
};

export const getStatementOfPurposeRequestData = (
  sopData: { [key: string]: boolean } | null,
  formData: FinancialInfoNEW,
  SopData: string[]
) => {
  if (!sopData) return [];

  const result: string[] = [];

  for (const data of SopData) {
    if (
      typeof formData[data as keyof typeof formData] === "boolean" &&
      formData[data as keyof typeof formData]
    ) {
      result.push(data);
    }
  }

  if (formData.otherPurpose) {
    return [...result, formData.otherPurpose];
  }

  return result;
};

export const mapPermKeyToComponent = (key: string) => {
  switch (key) {
    case "exchangerate":
      return "Exchange";
    case "transaction":
      return "Transactions";
    case "companydetails":
      return "Settings";
    default:
      return null;
  }
};

export const mapComponentToPermKey = (
  component: ComponentType
): string | null => {
  switch (component) {
    case "Exchange":
      return "exchangerate";
    case "Transactions":
      return "transaction";
    case "Settings":
      return "companydetails";
    default:
      return null;
  }
};

export const checkPermissionByKey = (
  permissionsList: string[],
  key: string
) => {
  return (
    permissionsList.find((perm) => perm.split("_")[1] === key) !== undefined
  );
};

export const checkPermissionByKeyAndValue = (
  permissionsList: string[],
  key: string,
  value: string
) => {
  return (
    permissionsList.find(
      (perm) => perm.split("_")[0] === key && perm.split("_")[1] === value
    ) !== undefined
  );
};

export const onRetry = () => {
  window.location.reload();
};

export const validatePdfFile = (
  file: File | null,
  fileSize: number = 10000
) => {
  if (!file) {
    alert("File is required");
    return false;
  }

  if (file.size > fileSize * 1024) {
    alert("File must be less than 10MB");
    return false;
  }

  return true;
};

export const isSNS = () => {
  // return true;
  return window.location.hostname.includes("bitramp.tech");
};

export const isBitramp = () => {
  // return true;
  return window.location.hostname.includes("bitramp.tech");
};

export function updateManifest() {
  if (typeof window === "undefined") return;

  const hostname = window.location.hostname;
  const isBitramp = true

  const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
  if (!manifestLink) return;

  const manifest = {
    name: isBitramp ? "Bitramp" : "Leonext",
    short_name: isBitramp ? "Bitramp" : "Leonext",
    description: "App for crypto and fiat transactions",
    start_url: "/pos/login",
    display: "standalone",
    background_color: "#f0e7db",
    theme_color: "#171717",
    scope: "/",
    orientation: "portrait",
    screenshots: isBitramp
        ? [
          { src: "/screenshot-1.png", sizes: "1918x862", type: "image/png" },
          { src: "/screenshot-2.png", sizes: "800x862", type: "image/png" },
        ]
        : [
          { src: "/screenshot-3.png", sizes: "1918x868", type: "image/png" },
          { src: "/screenshot-4.png", sizes: "800x868", type: "image/png" },
        ],
    icons: isBitramp
        ? [
          { src: "/android-launchericon-192x192.png", sizes: "192x192", type: "image/png", purpose: "favicon" },
          { src: "/android-launchericon-512x512.png", sizes: "512x512", type: "image/png", purpose: "favicon" },
          { src: "/apple-touch-iconb.png", sizes: "180x180", type: "image/png", purpose: "apple touch icon" },
          { src: "/maskable_iconb.png", sizes: "512x512", type: "image/png", purpose: "any maskable" },
          { src: "/f-144x144b.png", sizes: "144x144", type: "image/png", purpose: "any" },
        ]
        : [
          { src: "/android-chrome-192x192.png", sizes: "192x192", type: "image/png", purpose: "favicon" },
          { src: "/android-chrome-512x512.png", sizes: "512x512", type: "image/png", purpose: "favicon" },
          { src: "/apple-touch-icon.png", sizes: "180x180", type: "image/png", purpose: "apple touch icon" },
          { src: "/maskable_icon.png", sizes: "512x512", type: "image/png", purpose: "any maskable" },
          { src: "/f-144x144.png", sizes: "144x144", type: "image/png", purpose: "any" },
        ],
  };

  // Replace the manifest link href with a blob
  const blob = new Blob([JSON.stringify(manifest)], { type: "application/json" });
  manifestLink.href = URL.createObjectURL(blob);

  console.log("Manifest updated for hostname:", hostname);
}


 